import lessonData from "./lessons.json";
import structureFixtures from "./data/structure.json";
import progressFixtures from "./data/progress.json";
import {
  getDemoAssignmentsList,
  getDemoAssignmentSummaries,
  getDemoAssignmentDetail,
  getDemoSubmissionCreate,
  getDemoExemptionCreate,
  getDemoAssetUpload
} from "./assignmentsDemoData";
import { hasAssessedTerms } from "@utils/assignmentsUtils";
import { getPresignedActivity } from "./presignedLearnosityLoader";
import {
  AchievementStatus,
  type AchievementApiItem
} from "@ts/interfaces/achievements/api/achievementsApiInterface";
import type { MostRecentWork } from "@ts/interfaces/progress/api/gradeProgressApiInterface";
import type { NotificationInterface } from "@ts/interfaces/student/api/studentAccountApiInterface";

type HttpMethod = "GET" | "POST";

/* ------------------------------------------------------------------ */
/*  Types for the compact demo lesson data (src/demo/lessons.json)    */
/* ------------------------------------------------------------------ */

type DemoActivityData = {
  uuid: string;
  items: Record<string, string>;
};

type DemoLessonData = {
  n: string; // name
  d: string; // description
  lid: number; // lesson_id
  cc: {
    // curriculum_connection
    curriculum_code: string;
    term_focus: string;
    lesson_focus: string;
    curriculum_description: string;
    deep_links: {
      id: number;
      deep_link: string;
      service: string;
      subtopic_title: string;
    }[];
  } | null;
  lo: string[]; // learning_objectives
  ci?: string; // cover_image_url
  a: Record<string, DemoActivityData>; // activities
};

type DemoSubjectMeta = {
  name: string;
  course_type: string;
  portal_subject_id: number;
  portal_grade_subject_id: number;
  portal_grade_subject_uuid: string;
  term_slugs: Record<string, string>; // term number -> slug
};

type DemoGradeData = {
  grade_code: number;
  name: string;
  subjects: DemoSubjectMeta[];
  lessons: Record<string, DemoLessonData>; // key: "slug/term/week/lesson"
};

type DemoLessonsFile = Record<string, DemoGradeData>;

/* ------------------------------------------------------------------ */
/*  Lesson data — imported statically, no async fetch needed          */
/* ------------------------------------------------------------------ */

const getLessonData = (): DemoLessonsFile => lessonData as DemoLessonsFile;

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const DEMO_STUDENT = {
  first_name: "Demo",
  last_name: "Student",
  avatar_key: 3,
  display_name: "Demo Student",
  parent_email: "parent.demo@euka.edu.au",
  username: "demo.student",
  lms_wp_active: false,
  lms_wp_url: "",
  password_updated_at: "2 days ago"
};

/* ------------------------------------------------------------------ */
/*  Demo persistence — survive page refresh and process restart       */
/* ------------------------------------------------------------------ */

/**
 * localStorage key for the demo's persistent state.
 *
 * Demo mode is a sandbox — without persistence, reviewers are sent
 * through the orientation walkthrough every time the dev server
 * restarts. Persisting `intro_screen_seen` and the set of grades that
 * have completed orientation makes the demo feel like a real account
 * across page refreshes and process restarts.
 *
 * To re-experience orientation, clear `euka-demo-orientation-state`
 * from localStorage (DevTools → Application → Local Storage).
 */
const DEMO_STORAGE_KEY = "euka-demo-orientation-state";

interface DemoPersistedState {
  introScreenSeen: boolean;
  completedGradeCodes: number[];
  lessonProgress: Record<number, MostRecentWork | null>;
}

const loadDemoPersistedState = (): DemoPersistedState => {
  try {
    if (typeof localStorage === "undefined") {
      return {
        introScreenSeen: false,
        completedGradeCodes: [],
        lessonProgress: {}
      };
    }
    const raw = localStorage.getItem(DEMO_STORAGE_KEY);
    if (!raw)
      return {
        introScreenSeen: false,
        completedGradeCodes: [],
        lessonProgress: {}
      };
    const parsed = JSON.parse(raw) as Partial<DemoPersistedState>;
    return {
      introScreenSeen: Boolean(parsed.introScreenSeen),
      completedGradeCodes: Array.isArray(parsed.completedGradeCodes)
        ? parsed.completedGradeCodes.filter(g => typeof g === "number")
        : [],
      lessonProgress: typeof parsed.lessonProgress === "object" && parsed.lessonProgress !== null
        ? parsed.lessonProgress
        : {}
    };
  } catch {
    return {
      introScreenSeen: false,
      completedGradeCodes: [],
      lessonProgress: {}
    };
  }
};

const persistDemoState = (state: DemoPersistedState): void => {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage unavailable (private browsing, quota) — ignore
  }
};

const _demoPersisted = loadDemoPersistedState();

const DEMO_USER_STATE = {
  intro_screen_seen: _demoPersisted.introScreenSeen
};

// Demo grades exposed on the grade switcher. Grade 5 (Primary) is marked
// `current` so it's the default selection on a fresh login — this lets
// reviewers see the Primary-only Fun Fact of the Day panel on the first
// render without needing to switch grades. Grade 8 (Secondary) and
// Grade 11 (Senior) are kept available so reviewers can flip between
// grades to verify the panel hides itself correctly on non-Primary
// dashboards (the backend's 204 contract is mirrored by the demo
// fixture at /fun-fact/today).
//
// Grade 5 starts with `orientation_completed: false` so reviewers see
// the grade orientation walkthrough on first login (the Header redirect
// fires because `orientation_enabled` is true in /user/header-details).
// Grades 8 and 11 stay completed so reviewers can still jump straight
// into dashboard, terms, and lesson flows for Secondary and Senior.
// Once a reviewer finishes the Grade 5 orientation,
// `POST /user/complete-orientation/50` flips this fixture to `true`
// in-memory (see getDefaultPostResponse), so a refresh does not force
// them back into the walkthrough during a single demo session.
// Demo grades match the real API's snake_case wire shape; the mapper in
// useUserData.ts converts the assignment-flag fields to camelCase.
// G8 deliberately has assignment data but the Secondary UI gate is OFF,
// to demonstrate the "flag, not data, is the gate" invariant — entry
// points should suppress to the legacy Portal link even when terms carry
// indexed assignment data.
const DEMO_GRADES = [
  {
    grade_code: 20,
    name: "Grade 2",
    status: "current",
    orientation_completed:
      _demoPersisted.completedGradeCodes.includes(20) || false,
    // BLD2-731: toggleable via `__demoPost('/demo/toggle-g2-assessment')`.
    // Defaults to true (assessed) so reviewers see the submit section by
    // default; G10 is the non-assessed default for bug reproduction.
    // F-G10: Portal does not yet define an assessment entitlement for this
    // grade group, so assessed_terms is always []. The marking-enabled check
    // in AssignmentsDashboardContainer uses the per-term marking signal.
    assessed_terms: [],
    // Enabled so the junior work-sample content blocks can be checked in
    // demo mode using the G2 Mathematics sample ("Teddy Bear Picnic Planner").
    assignments_in_learning_enabled: true,
    assignment_detail_in_learning_enabled: true,
    // BLD2-731: enabled so the submit panel is visible for assessed/non-assessed
    // toggle testing (previously false — no functional change for non-assessed).
    assignment_submit_panel_enabled: true,
    assignment_work_sample_uploads_enabled: true
  },
  {
    grade_code: 50,
    name: "Grade 5",
    status: "current",
    orientation_completed:
      _demoPersisted.completedGradeCodes.includes(50) || false,
    // BLD2-731: toggleable via `__demoPost('/demo/toggle-g5-assessment')`.
    // Default non-assessed; toggle sets assessed_terms: [4] (T4 only).
    // BLD2-852: T4 assessed for testing marking-enabled Primary theme.
    // G5 has demo assignments in T1-T4; T4 verifies the assessed variant
    // (submit panel, status badges) while T1-T3 verify the non-assessed variant.
    assessed_terms: [4],
    assignments_in_learning_enabled: true,
    assignment_detail_in_learning_enabled: true,
    // BLD2-731: enabled for submit panel toggle testing.
    assignment_submit_panel_enabled: true,
    // BLD2-676: Slice 9 work-sample panel visible on the assignment detail page.
    assignment_work_sample_uploads_enabled: true
  },
  {
    grade_code: 80,
    name: "Grade 8",
    status: "active",
    orientation_completed: true,
    // BLD2-731: toggleable via `__demoPost('/demo/toggle-g8-assessment')`.
    assessed_terms: [], // non-assessed by default; toggle sets [1]
    // OFF by default — demonstrates the "flag, not data, is the gate" invariant
    // (sidebar suppresses the Assignments link even though term data is present).
    // Flip to true and re-serve to test the full Secondary assignments flow
    // end-to-end (BLD2-666): sidebar link → list page → Secondary theming.
    assignments_in_learning_enabled: false,
    assignment_detail_in_learning_enabled: false,
    // BLD2-731: enabled for submit panel toggle testing (assignments UI gate
    // must also be enabled above before the submit panel is reachable).
    assignment_submit_panel_enabled: true,
    assignment_work_sample_uploads_enabled: true
  },
  {
    grade_code: 100,
    name: "Grade 10",
    status: "active",
    orientation_completed: true,
    // BLD2-731: defaults to false (non-assessed) — this is the primary grade
    // for reproducing the bug. Toggle via `__demoPost('/demo/toggle-g10-assessment')`.
    // BLD2-852: T3 only assessed for testing marking-enabled Secondary theme.
    // T1, T2 and T4 are non-assessed — both UI states testable within G10.
    // T3 simulates a realistic partial-term entitlement (e.g. skipped T2).
    assessed_terms: [3],
    // Enabled so the work-sample content blocks (Slices 3–9) can be verified
    // in demo mode. Two T1 assignments are available: "Letter to Parliament"
    // (Civics, real BE data) and "Analysing a Speech" (English).
    // See assignmentContentG10 / assignmentContentG10English in
    // apps/learning/src/demo/data/assignment-content.ts.
    assignments_in_learning_enabled: true,
    assignment_detail_in_learning_enabled: true,
    // BLD2-731: enabled so the non-assessed bug (disabled submit button visible)
    // can be reproduced and verified fixed in demo mode.
    assignment_submit_panel_enabled: true,
    assignment_work_sample_uploads_enabled: true
  },
  {
    grade_code: 110,
    name: "Grade 11",
    status: "active",
    orientation_completed: true,
    // Reviewers can toggle between assessed (UPC) and non-assessed pathways
    // by running `__demoPost('/demo/toggle-g11-assessment')` in the browser console.
    // BLD2-853 AC6: T1 and T2 are marking-enabled by default; T3 and T4 are not.
    // This lets reviewers verify that navigating to the T3 assignment list
    // shows no status badges even though T1/T2 do — the per-term check in
    // AssignmentsDashboardContainer drives this.
    assessed_terms: [1, 2, 4], // Default: UPC pathway; T4 added for BLD2-1038 fully-exempted demo
    assignments_in_learning_enabled: true,
    assignment_detail_in_learning_enabled: true,
    assignment_submit_panel_enabled: true,
    // BLD2-676: Work samples are F-10 only. getIsAssignmentWorkSampleUploadsEnabled
    // returns false for senior grades regardless of this flag.
    assignment_work_sample_uploads_enabled: false
  },
  {
    grade_code: 120,
    name: "Grade 12",
    status: "active",
    orientation_completed: true,
    // Non-assessed senior grade for testing that pathway with assignments.
    assessed_terms: [], // Senior, non-UPC: no marking entitlement
    assignments_in_learning_enabled: true,
    assignment_detail_in_learning_enabled: true,
    assignment_submit_panel_enabled: true,
    // BLD2-676: Work samples are F-10 only. getIsAssignmentWorkSampleUploadsEnabled
    // returns false for senior grades regardless of this flag.
    assignment_work_sample_uploads_enabled: false
  }
];

const getGradeName = (gradeCode: number): string => {
  const found = DEMO_GRADES.find(grade => grade.grade_code === gradeCode);
  return found?.name ?? `Grade ${gradeCode}`;
};

/* ------------------------------------------------------------------ */
/*  PLP (Personalised Learning Pathway) configuration                 */
/* ------------------------------------------------------------------ */

/**
 * Maps a host grade code to the source grade whose subjects should
 * appear as PLP subjects under the host grade.
 *
 * Example: Grade 8 (80) gets Grade 11 (110) subjects as PLPs,
 * excluding any that overlap with Grade 8's own subjects.
 */
const PLP_CONFIG: Record<number, number> = {
  80: 110
};

type SubjectWithPlp = DemoSubjectMeta & {
  plp_grade_code: number;
  plp_grade_name: string;
  /** The grade code whose lesson data this subject uses. */
  source_grade_code: number;
};

/* ------------------------------------------------------------------ */
/*  Subject helpers                                                   */
/* ------------------------------------------------------------------ */

/**
 * Demo-only fallback grade for lesson content. Some demo grades appear in
 * DEMO_GRADES to unblock feature testing (e.g. Grade 2 for the junior
 * work-sample flow) without shipping full curriculum data in lessons.json.
 * When a requested grade has no lessons.json entry we source subjects from
 * Grade 5 (Primary), which is a first-class demo grade. Production reads
 * from the real API and never hits this branch.
 */
const DEMO_LESSON_FALLBACK_GRADE_CODE = 50;

const getGradeSubjects = (gradeCode: number): DemoSubjectMeta[] | undefined => {
  const data = getLessonData();
  return (
    data?.[String(gradeCode)]?.subjects ??
    data?.[String(DEMO_LESSON_FALLBACK_GRADE_CODE)]?.subjects
  );
};

/**
 * Returns the full subject list for a grade, including any PLP subjects
 * sourced from another grade (per PLP_CONFIG).
 */
const getGradeSubjectsWithPlps = (gradeCode: number): SubjectWithPlp[] => {
  const data = getLessonData();
  const hasOwnData = !!data?.[String(gradeCode)];
  // When we fall back to the demo fallback grade, downstream lesson lookups
  // must resolve against that grade's data, not the requested grade — so
  // source_grade_code points at whichever grade actually has content.
  const sourceGradeCode = hasOwnData
    ? gradeCode
    : DEMO_LESSON_FALLBACK_GRADE_CODE;
  const ownSubjects = (getGradeSubjects(gradeCode) ?? []).map(s => ({
    ...s,
    plp_grade_code: 0,
    plp_grade_name: "",
    source_grade_code: sourceGradeCode
  }));

  const plpSourceGrade = PLP_CONFIG[gradeCode];
  if (!plpSourceGrade) return ownSubjects;

  const plpSourceSubjects = getGradeSubjects(plpSourceGrade) ?? [];
  const ownNames = new Set(ownSubjects.map(s => s.name));
  const plpGradeName = getGradeName(plpSourceGrade);

  const plpSubjects: SubjectWithPlp[] = plpSourceSubjects
    .filter(s => !ownNames.has(s.name))
    .map(s => ({
      ...s,
      plp_grade_code: plpSourceGrade,
      plp_grade_name: plpGradeName,
      source_grade_code: plpSourceGrade
    }));

  return [...ownSubjects, ...plpSubjects];
};

/* Demo notifications per grade (feeds /user/dashboard-details). */
const getDemoNotifications = (gradeCode: number): NotificationInterface[] => {
  if (gradeCode === 20 || gradeCode === 80) {
    return [
      {
        created_at: "2026-01-10T00:00:00Z",
        euka_id: DEMO_STUDENT.username,
        id: gradeCode === 20 ? 103 : 102,
        notification_type: "new_term_available",
        ref_for_generated_at: {
          generated_at: "2026-01-10T00:00:00Z",
          id: 2,
          model: "term",
          student_grade: getGradeName(gradeCode),
          term_number: 2,
          grade_code: gradeCode
        },
        resolved_at: ""
      }
    ];
  }

  if (gradeCode === 110) {
    return [
      {
        created_at: "2026-01-10T00:00:00Z",
        euka_id: DEMO_STUDENT.username,
        id: 101,
        notification_type: "complete_subject_selection",
        ref_for_generated_at: {
          generated_at: "2026-01-10T00:00:00Z",
          id: 1,
          model: "student",
          student_grade: getGradeName(gradeCode)
        },
        resolved_at: ""
      }
    ];
  }

  return [];
};

/* ------------------------------------------------------------------ */
/*  Demo lesson progress tracking (BLD2-875, BLD2-877)                */
/* ------------------------------------------------------------------ */

/**
 * Get the current lesson progress state for a grade.
 * Returns null if no lessons have been started (or if explicitly reset).
 */
const getDemoLessonProgress = (gradeCode: number): MostRecentWork | null => {
  return _demoPersisted.lessonProgress[gradeCode] ?? null;
};

/**
 * Update lesson progress when a lesson is started or completed.
 * Persists to localStorage so it survives page refresh.
 */
const updateDemoLessonProgress = (
  gradeCode: number,
  termNumber: number,
  weekNumber: number,
  subjectSlug: string,
  subjectName: string
): void => {
  _demoPersisted.lessonProgress[gradeCode] = {
    week_number: weekNumber,
    term_number: termNumber,
    subject_slug: subjectSlug,
    subject_name: subjectName,
    all_lessons_complete: false // Demo never marks all complete
  };
  persistDemoState(_demoPersisted);
};

/**
 * Reset lesson progress for a grade to null (not-started state).
 * Used by browser console test helpers.
 */
const resetDemoLessonProgress = (gradeCode: number): void => {
  delete _demoPersisted.lessonProgress[gradeCode];
  persistDemoState(_demoPersisted);
};

/* ------------------------------------------------------------------ */
/*  Elective subject configuration for demo certificates             */
/* ------------------------------------------------------------------ */

/**
 * Configuration for demo elective subjects.
 * - courseNames: Display names for each term's course (used for certificates)
 * - completedTerms: Which terms have all lessons completed (for demo certificates)
 *   - true = all 30 lessons completed (certificate shows as enabled/complete)
 *   - false = 0 lessons completed (certificate shows as disabled)
 */
type ElectiveConfig = {
  courseNames: [string, string, string, string];
  completedTerms: [boolean, boolean, boolean, boolean];
};

const DEMO_ELECTIVE_CONFIG: Record<number, ElectiveConfig> = {
  // Business Studies (portal_subject_id: 200) - All terms completed
  200: {
    courseNames: [
      "Business Operations",
      "Marketing & Finance",
      "Human Resources",
      "Business Planning"
    ],
    completedTerms: [true, true, true, true]
  },
  // Design & Technology (portal_subject_id: 201) - Only first term completed
  201: {
    courseNames: [
      "Design Fundamentals",
      "Materials & Production",
      "Innovation & Prototyping",
      "Industry Applications"
    ],
    completedTerms: [true, false, false, false]
  }
};

/* ------------------------------------------------------------------ */
/*  Build section content (real data — Learnosity UUIDs)              */
/* ------------------------------------------------------------------ */

/**
 * Activity bundling rules matching the backend:
 * - "learn"    -> ["textbook", "learn"]
 * - "apply"    -> ["apply", "textbook", "learn"]
 * - "practise" -> ["practise", "textbook", "learn"]
 * - "summary"  -> ["apply", "reflection"]
 * - others     -> [section]
 */
const getActivitySlugs = (section: string): string[] => {
  switch (section.toLowerCase()) {
    case "learn":
      return ["textbook", "learn"];
    case "apply":
      return ["apply", "textbook", "learn"];
    case "practise":
      return ["practise", "textbook", "learn"];
    case "summary":
      return ["apply", "reflection"];
    default:
      return [section];
  }
};

/**
 * Resolves the actual grade code whose lesson data a subject slug belongs to.
 * For PLP subjects the slug belongs to the source grade, not the host grade.
 */
const resolveSourceGradeCode = (
  hostGradeCode: number,
  subjectSlug: string
): number => {
  const plpSourceGrade = PLP_CONFIG[hostGradeCode];
  if (!plpSourceGrade) return hostGradeCode;

  const plpSubjects = getGradeSubjects(plpSourceGrade) ?? [];
  const isPlpSlug = plpSubjects.some(s =>
    Object.values(s.term_slugs).includes(subjectSlug)
  );
  return isPlpSlug ? plpSourceGrade : hostGradeCode;
};

const buildSectionContent = (
  gradeCode: number,
  termId: number,
  subjectSlug: string,
  weekId: number,
  lessonId: number,
  section: string
) => {
  const data = getLessonData();

  // Resolve the actual grade whose data this subject slug belongs to (PLP-aware)
  const sourceGradeCode = resolveSourceGradeCode(gradeCode, subjectSlug);
  const grade = data?.[String(sourceGradeCode)];
  if (!grade) return buildFallbackSectionContent(section, lessonId);

  // Find the lesson by searching for matching lesson_id under this subject/term/week
  let lesson: DemoLessonData | undefined;
  for (let lessonNum = 1; lessonNum <= 50; lessonNum++) {
    const key = `${subjectSlug}/${termId}/${weekId}/${lessonNum}`;
    const candidate = grade.lessons[key];
    if (candidate && candidate.lid === lessonId) {
      lesson = candidate;
      break;
    }
  }

  if (!lesson) return buildFallbackSectionContent(section, lessonId);

  // Gather activity items based on bundling rules
  const activitySlugs = getActivitySlugs(section);
  let activityUuid = "";
  const allItemUuids: Record<string, string> = {};
  const extraActivityItemUuids: Record<string, Record<string, string>> = {};

  for (const actSlug of activitySlugs) {
    const activity = lesson.a[actSlug];
    if (!activity || !activity.uuid) continue;

    if (!activityUuid) {
      activityUuid = activity.uuid;
    }

    for (const [itemKey, itemUuid] of Object.entries(activity.items)) {
      allItemUuids[itemKey] = itemUuid;
    }

    // Build the per-activity item uuid maps
    const mapKey = `${actSlug}_activity_item_uuids`;
    extraActivityItemUuids[mapKey] = { ...activity.items };
  }

  // Build the items list for the Learnosity activity_request
  const itemsList = Object.values(allItemUuids);

  // Check for build-time pre-signed data (generated by presign-learnosity.mjs
  // during CI for static S3 deploys). When available the activity_request is
  // already signed and the client can skip the runtime /learnosity-sign fetch.
  const presigned = getPresignedActivity(activityUuid, itemsList);

  const activityRequest = presigned
    ? presigned
    : {
        // Local dev fallback — the httpClient will call the Vite dev server's
        // /learnosity-sign endpoint to sign this on-demand.
        _unsigned: true,
        _activity_uuid: activityUuid,
        _item_uuids: itemsList,
        request: {
          request: {
            items: itemsList
          }
        }
      };

  return {
    activity_request: activityRequest,
    curriculum_connection: lesson.cc,
    learning_objectives: lesson.lo,
    list_of_materials: [],
    term_based_messages: [],
    status: "started",
    lesson_id: lesson.lid,
    cover_image_url: lesson.ci || "",
    // BLD2-463: enable the Read Aloud AudioPlayer and notification panel
    // in demo mode so the feature can be exercised without a real Polly
    // deployment. Uses a short public MP3 as the audio source.
    audio_enabled: true,
    audio_paths: {
      learn: "https://www.w3schools.com/html/horse.mp3",
      textbook: "https://www.w3schools.com/html/horse.mp3"
    },
    audio_banner_visible: true,
    ...extraActivityItemUuids
  };
};

const buildFallbackSectionContent = (section: string, lessonId: number) => ({
  activity_request: {
    request: {
      request: {
        items: [`demo-${section}-item-${lessonId}`]
      }
    }
  },
  curriculum_connection: null,
  learning_objectives: [
    "Understand the key concept for this lesson.",
    "Apply learning in a short activity."
  ],
  list_of_materials: [],
  term_based_messages: [],
  status: "started",
  lesson_id: lessonId,
  cover_image_url: "",
  apply_activity_item_uuids: { apply: `demo-apply-${lessonId}` },
  reflection_activity_item_uuids: {
    reflection: `demo-reflection-${lessonId}`
  },
  learn_activity_item_uuids: { learn: `demo-learn-${lessonId}` },
  textbook_activity_item_uuids: { textbook: `demo-textbook-${lessonId}` },
  // BLD2-463: mirror buildSectionContent — enable AudioPlayer + notification
  // panel in demo fallback paths (lessons without full fixture data).
  audio_enabled: true,
  audio_paths: {
    learn: "https://www.w3schools.com/html/horse.mp3",
    textbook: "https://www.w3schools.com/html/horse.mp3"
  },
  audio_banner_visible: true
});

/* ------------------------------------------------------------------ */
/*  Build grade materials                                             */
/* ------------------------------------------------------------------ */

const buildGradeMaterials = () => ({
  materials: [
    {
      name: "A4 Exercise Book",
      image: "",
      adult_supervision: false,
      type: "Essential",
      category: "Stationery",
      use: "General lesson activities"
    },
    {
      name: "Graph Paper Pad",
      image: "",
      adult_supervision: false,
      type: "Essential",
      category: "Mathematics",
      use: "Math tasks and plotting"
    },
    {
      name: "Paint Set",
      image: "",
      adult_supervision: true,
      type: "Practical",
      category: "Creative Arts",
      use: "Hands-on practical projects"
    },
    {
      name: "Novel Study Reader",
      image: "",
      adult_supervision: false,
      type: "Book",
      category: "English",
      use: "Guided reading"
    }
  ]
});

/* ------------------------------------------------------------------ */
/*  Build achievements                                                */
/* ------------------------------------------------------------------ */

const DEMO_EXCELLENCE_CERTIFICATES_BY_GRADE: Record<
  number,
  {
    type: "excellence";
    certificate_name: string;
    pathPrefix: string;
  }[]
> = {
  50: [
    {
      type: "excellence",
      certificate_name: "Excellence in Execution",
      pathPrefix: "certificates/excellence-execution"
    }
  ],
  110: [
    {
      type: "excellence",
      certificate_name: "Impact and Purpose",
      pathPrefix: "certificates/excellence-impact"
    },
    {
      type: "excellence",
      certificate_name: "Creativity and Innovation",
      pathPrefix: "certificates/excellence-creativity"
    }
  ]
};

/**
 * Simulates certificate request endpoint response.
 * In demo mode, always returns "processing" status to show the modal.
 * Real API would trigger certificate generation and return actual status.
 */
const buildCertificateRequestResponse = () => ({
  status: "processing" as const,
  message: "Certificate request submitted successfully"
});

const buildAchievements = (gradeCode: number) => {
  const achievements: AchievementApiItem[] = [
    {
      euka_id: DEMO_STUDENT.username,
      grade: String(gradeCode),
      term: "1",
      status: AchievementStatus.Complete,
      type: "term_completion" as const,
      certificate_name: "",
      url: `${window.location.origin}/demo-assets/certificates/term-1.pdf`
    },
    {
      euka_id: DEMO_STUDENT.username,
      grade: String(gradeCode),
      term: "2",
      status: AchievementStatus.Processing,
      type: "term_completion" as const,
      certificate_name: "",
      url: ""
    },
    {
      euka_id: DEMO_STUDENT.username,
      grade: String(gradeCode),
      term: "3",
      status: AchievementStatus.Complete,
      type: "term_completion" as const,
      certificate_name: "",
      url: ""
    }
  ];

  // Add assessed pathway certificates for senior grades (11, 12)
  const isAssessedGrade = hasAssessedTerms(DEMO_GRADES.find(g => g.grade_code === gradeCode));

  if (isAssessedGrade) {
    // Assignment completion certificate - demo shows as processing (awaiting generation)
    achievements.push({
      euka_id: DEMO_STUDENT.username,
      grade: String(gradeCode),
      term: "0",
      status: AchievementStatus.Processing,
      type: "assignment_completion" as const,
      certificate_name: "",
      url: "" // Empty URL since certificate is still processing
    });
    // University preparation certificate is omitted so it shows as incomplete/disabled
  }

  const enabledExcellenceCertificates =
    DEMO_EXCELLENCE_CERTIFICATES_BY_GRADE[gradeCode];

  if (enabledExcellenceCertificates?.length) {
    achievements.push(
      ...enabledExcellenceCertificates.map(certificate => ({
        euka_id: DEMO_STUDENT.username,
        grade: String(gradeCode),
        term: "",
        status: AchievementStatus.Complete,
        type: certificate.type,
        certificate_name: certificate.certificate_name,
        url: `${window.location.origin}/demo-assets/${certificate.pathPrefix}.pdf`
      }))
    );
  }

  // Add elective completion certificates for Grade 10 (100)
  // Each elective course (per term) gets its own certificate
  if (gradeCode === 100) {
    Object.entries(DEMO_ELECTIVE_CONFIG).forEach(([subjectId, config]) => {
      // Add course_completion certificate for each completed term
      config.completedTerms.forEach((isCompleted, termIndex) => {
        if (isCompleted) {
          const termNumber = termIndex + 1;
          const courseName = config.courseNames[termIndex];
          achievements.push({
            euka_id: DEMO_STUDENT.username,
            grade: String(gradeCode),
            term: String(termNumber),
            status: AchievementStatus.Complete,
            type: "course_completion" as const,
            certificate_name: courseName,
            subject_id: Number(subjectId),
            url: `${window.location.origin}/demo-assets/certificates/course-completion-${courseName}.pdf`
          });
        }
      });

      // Add elective_completion certificate if ALL terms are completed
      const allTermsCompleted = config.completedTerms.every(
        completed => completed
      );
      if (allTermsCompleted) {
        achievements.push({
          euka_id: DEMO_STUDENT.username,
          grade: String(gradeCode),
          term: "4", // Last term (but this is ignored for elective_completion key matching)
          status: AchievementStatus.Complete,
          type: "elective_completion" as const,
          certificate_name: "", // Subject name will be used from subjects data
          subject_id: Number(subjectId),
          url: `${window.location.origin}/demo-assets/certificates/elective-completion-${subjectId}.pdf`
        });
      }
    });
  }

  return achievements;
};

/* ------------------------------------------------------------------ */
/*  Build signed URL placeholders                                     */
/* ------------------------------------------------------------------ */

const buildDemoSignedUrl = (pathPrefix: string) => {
  const { origin } = window.location;
  return {
    success: true,
    message: "ok",
    data: {
      signed_url: `${origin}/demo-assets/${pathPrefix}.pdf`
    }
  };
};

/* ------------------------------------------------------------------ */
/*  POST responses                                                    */
/* ------------------------------------------------------------------ */

/**
 * Handle `POST /user/complete-orientation/:gradeCode` for the demo.
 *
 * Flips the in-memory DEMO_GRADES entry AND persists to localStorage so
 * the completion survives both page refreshes and dev-server restarts.
 * To replay orientation, clear `euka-demo-orientation-state` from
 * localStorage.
 */
const maybeCompleteOrientation = (
  path: string
): { success: true; message: string } | undefined => {
  const match = path.match(/^\/user\/complete-orientation\/(\d+)$/);
  if (!match) return undefined;

  const gradeCode = Number(match[1]);
  const grade = DEMO_GRADES.find(g => g.grade_code === gradeCode);
  if (grade) {
    grade.orientation_completed = true;
  }

  // Persist so process restart doesn't force the user back through orientation
  if (!_demoPersisted.completedGradeCodes.includes(gradeCode)) {
    _demoPersisted.completedGradeCodes.push(gradeCode);
    persistDemoState(_demoPersisted);
  }

  return { success: true, message: "ok" };
};

// In-memory demo store; reset on page reload. One sample per assignment.
type DemoAssignmentWorkSample = {
  uuid: string;
  comment: string;
  attachments: Array<{
    id: string;
    name: string;
    full_size_url: string;
    thumbnail_url: string;
    mime_type: string;
    status: "completed";
  }>;
};
const _demoAssignmentWorkSamples: Map<string, DemoAssignmentWorkSample> =
  new Map();

const handleAssignmentWorkSamplePost = (
  assignmentUuid: string,
  payload?: unknown
): { uuid: string; attachments: Array<Record<string, unknown>> } => {
  const body = (payload ?? {}) as {
    comment?: string;
    work_sample_uuid?: string;
    attachments?: Array<{
      file_id: string;
      file_name: string;
      mime_type?: string;
      file_size?: number;
      action: "new" | "deleted" | "failed";
    }>;
  };

  const existing = _demoAssignmentWorkSamples.get(assignmentUuid);
  const uuid = body.work_sample_uuid ?? existing?.uuid ?? `demo-aws-${assignmentUuid}`;
  const comment = body.comment ?? existing?.comment ?? "";

  const newAttachmentInputs = (body.attachments ?? []).filter(
    a => a.action === "new"
  );
  const deletedIds = new Set(
    (body.attachments ?? [])
      .filter(a => a.action === "deleted")
      .map(a => a.file_id)
  );

  const previous = (existing?.attachments ?? []).filter(
    a => !deletedIds.has(a.id)
  );

  const nextAttachments = [
    ...previous,
    ...newAttachmentInputs.map(a => ({
      id: a.file_id,
      name: a.file_name,
      full_size_url: `https://demo.local/work-sample/${a.file_id}`,
      thumbnail_url: `https://demo.local/work-sample/${a.file_id}?thumb=1`,
      mime_type: a.mime_type ?? "image/jpeg",
      status: "completed" as const
    }))
  ];

  _demoAssignmentWorkSamples.set(assignmentUuid, {
    uuid,
    comment,
    attachments: nextAttachments
  });

  return {
    uuid,
    // Empty attachments array skips the browser -> S3 PUT loop entirely in
    // demo mode. The saved sample is still visible via the GET handler.
    attachments: []
  };
};

const getChatMessageResponse = (payload?: unknown) => {
  const message =
    typeof payload === "object" && payload !== null && "message" in payload
      ? String((payload as { message?: string }).message ?? "")
      : "";

  return {
    session_id: "demo-session-1",
    response: message
      ? `Demo tutor response for: ${message}`
      : "Demo tutor response"
  };
};

const getDefaultPostResponse = (path: string, payload?: unknown) => {
  if (path === "/chat/messages") {
    return getChatMessageResponse(payload);
  }

  if (path === "/chat/clear") {
    return { status: "cleared" };
  }

  if (path === "/user/state") {
    if (typeof payload === "object" && payload !== null && "key" in payload) {
      const { key, value } = payload as { key: string; value: boolean };
      if (key === "intro_screen_seen") {
        DEMO_USER_STATE.intro_screen_seen = Boolean(value);
        // Persist so process restart doesn't replay the intro screen
        _demoPersisted.introScreenSeen = Boolean(value);
        persistDemoState(_demoPersisted);
      }
    }

    return { success: true, message: "ok" };
  }

  const completeOrientationResponse = maybeCompleteOrientation(path);
  if (completeOrientationResponse) {
    return completeOrientationResponse;
  }

  // ── Demo assessed-terms toggle utilities (BLD2-915) ──────────────────────
  //
  // Each grade has a "canonical" assessed_terms list that matches the demo
  // fixtures (G5: T4, G10: T3, G11: T1+T2). The toggle flips between that
  // list and an empty array (non-marking).
  //
  // Call from the browser console, then refresh the page:
  //   __demoPost('/demo/toggle-g11-assessment')
  //   __demoPost('/demo/toggle-g5-assessment')   etc.
  //
  // Helper utilities used below:
  //   setAssessedTerms(grade, terms)  → set specific terms
  //   toggleAssessedTerms(grade, terms) → flip between terms and []
  const setAssessedTerms = (
    grade: (typeof DEMO_GRADES)[number],
    terms: number[]
  ) => {
    grade.assessed_terms = terms;
  };
  const toggleAssessedTerms = (
    grade: (typeof DEMO_GRADES)[number],
    defaultTerms: number[]
  ) => {
    setAssessedTerms(grade, hasAssessedTerms(grade) ? [] : defaultTerms);
  };

  if (path === "/demo/toggle-g11-assessment") {
    const g11 = DEMO_GRADES.find(g => g.grade_code === 110);
    if (g11) toggleAssessedTerms(g11, [1, 2, 4]);
    persistDemoState(_demoPersisted);
    return { success: true, assessed_terms: g11?.assessed_terms ?? [] };
  }

  // BLD2-731 / BLD2-915: toggle assessed_terms for each F-G10 demo grade.
  // Call from browser console, then refresh:
  //   __demoPost('/demo/toggle-g2-assessment')
  //   __demoPost('/demo/toggle-g5-assessment')
  //   __demoPost('/demo/toggle-g8-assessment')
  //   __demoPost('/demo/toggle-g10-assessment')   ← primary repro grade
  if (path === "/demo/toggle-g2-assessment") {
    const g2 = DEMO_GRADES.find(g => g.grade_code === 20);
    if (g2) toggleAssessedTerms(g2, [1, 2, 3, 4]);
    persistDemoState(_demoPersisted);
    return { success: true, assessed_terms: g2?.assessed_terms ?? [] };
  }
  if (path === "/demo/toggle-g5-assessment") {
    const g5 = DEMO_GRADES.find(g => g.grade_code === 50);
    if (g5) toggleAssessedTerms(g5, [4]); // T4 is the assessed demo term for G5
    persistDemoState(_demoPersisted);
    return { success: true, assessed_terms: g5?.assessed_terms ?? [] };
  }
  if (path === "/demo/toggle-g8-assessment") {
    const g8 = DEMO_GRADES.find(g => g.grade_code === 80);
    if (g8) toggleAssessedTerms(g8, [1, 2, 3, 4]);
    persistDemoState(_demoPersisted);
    return { success: true, assessed_terms: g8?.assessed_terms ?? [] };
  }
  if (path === "/demo/toggle-g10-assessment") {
    const g10 = DEMO_GRADES.find(g => g.grade_code === 100);
    if (g10) toggleAssessedTerms(g10, [3]); // T3 is the assessed demo term for G10
    persistDemoState(_demoPersisted);
    return { success: true, assessed_terms: g10?.assessed_terms ?? [] };
  }

  // ── Demo lesson progress test utilities (BLD2-875, BLD2-877) ────────────────
  //
  // Set up specific lesson progress states for testing the dashboard welcome
  // panel's cache invalidation behavior.
  //
  // Test Scenario 1 (First lesson start - BLD2-875):
  //   1. __demoPost('/demo/reset-lesson-progress-g11')
  //   2. Refresh page, go to G11 dashboard (shows "Start lessons")
  //   3. Start G11 T1 W1 English L1
  //   4. Navigate back → BUG: still shows "Start lessons"
  //   5. Refresh → FIXED: shows "TERM 1 Week 1"
  //
  // Test Scenario 2 (Resume update - BLD2-877):
  //   1. __demoPost('/demo/test-start-lesson-g11')
  //   2. Refresh page, go to G11 dashboard (shows "TERM 1 Week 1")
  //   3. Start G11 T1 W2 English L1
  //   4. Navigate back → BUG: still shows "TERM 1 Week 1"
  //   5. Refresh → FIXED: shows "TERM 1 Week 2"

  if (path === "/demo/test-start-lesson-g11") {
    updateDemoLessonProgress(110, 1, 1, "english", "English");
    return { 
      success: true, 
      message: "G11 lesson progress set to Term 1, Week 1, English",
      progress: getDemoLessonProgress(110) 
    };
  }

  if (path === "/demo/test-start-lesson-g5") {
    updateDemoLessonProgress(50, 1, 1, "mathematics", "Mathematics");
    return { 
      success: true,
      message: "G5 lesson progress set to Term 1, Week 1, Mathematics",
      progress: getDemoLessonProgress(50) 
    };
  }

  if (path === "/demo/reset-lesson-progress-g11") {
    resetDemoLessonProgress(110);
    return { 
      success: true,
      message: "G11 lesson progress cleared (not-started state)",
      progress: null 
    };
  }

  if (path === "/demo/reset-lesson-progress-g5") {
    resetDemoLessonProgress(50);
    return { 
      success: true,
      message: "G5 lesson progress cleared (not-started state)",
      progress: null 
    };
  }

  if (path === "/impersonate/stop") {
    return { redirect: "/portal" };
  }

  // BLD2-463: acknowledge banner dismissal in demo mode. The in-session
  // isBannerDismissed state flip in the container handles the UI instantly;
  // this stub ensures the POST doesn't fall through to an unhandled response.
  if (path === "/user/audio-banner-dismissed") {
    return { success: true, audio_banner_visible: false };
  }

  if (path === "/lesson/work-sample") {
    return {
      id: "demo-work-sample-1",
      comment: "Saved in demo mode",
      attachments: []
    };
  }

  // BLD2-676: assignment-scoped work sample POST
  const assignmentWorkSamplePostMatch = path.match(
    /^\/assignments\/([^/]+)\/work-sample$/
  );
  if (assignmentWorkSamplePostMatch) {
    return handleAssignmentWorkSamplePost(
      assignmentWorkSamplePostMatch[1],
      payload
    );
  }

  // Lesson start endpoint — updates tracked progress (BLD2-875, BLD2-877)
  const lessonStartMatch = path.match(
    /^\/lesson\/grade\/(\d+)\/term\/(\d+)\/subject\/([^/]+)\/week\/(\d+)\/lesson\/\d+\/start$/
  );
  if (lessonStartMatch) {
    const [, gradeRaw, termRaw, subjectSlug, weekRaw] = lessonStartMatch;
    const gradeCode = Number(gradeRaw);
    const termNumber = Number(termRaw);
    const weekNumber = Number(weekRaw);
    
    // Extract subject name from the grade data
    const subjects = getGradeSubjectsWithPlps(gradeCode);
    const subject = subjects.find(s => {
      const slug = s.term_slugs[termRaw] ?? "";
      return slug === subjectSlug;
    });
    const subjectName = subject?.name ?? "Unknown";
    
    updateDemoLessonProgress(
      gradeCode,
      termNumber,
      weekNumber,
      subjectSlug,
      subjectName
    );
    
    return { success: true, message: "Lesson started" };
  }

  // Lesson /finish endpoint — returns a deferred CSAT trigger AND updates progress
  // (BLD2-875, BLD2-877)
  const lessonFinishMatch = path.match(
    /^\/lesson\/grade\/(\d+)\/term\/(\d+)\/subject\/([^/]+)\/week\/(\d+)\/lesson\/\d+\/finish$/
  );
  if (lessonFinishMatch) {
    const [, gradeRaw, termRaw, subjectSlug, weekRaw] = lessonFinishMatch;
    const gradeCode = Number(gradeRaw);
    const termNumber = Number(termRaw);
    const weekNumber = Number(weekRaw);
    
    // Extract subject name
    const subjects = getGradeSubjectsWithPlps(gradeCode);
    const subject = subjects.find(s => {
      const slug = s.term_slugs[termRaw] ?? "";
      return slug === subjectSlug;
    });
    const subjectName = subject?.name ?? "Unknown";
    
    // Update progress on completion too
    updateDemoLessonProgress(
      gradeCode,
      termNumber,
      weekNumber,
      subjectSlug,
      subjectName
    );
    
    return {
      csat_trigger: {
        should_fire: true,
        deferred: true,
        grade: gradeCode,
        lifetime_lessons_completed: 3,
        trigger_subject_name: subjectName,
        trigger_lesson_term: termNumber,
        trigger_lesson_week: weekNumber,
        trigger_lesson_number: 1
      }
    };
  }

  // Assignment POST endpoints
  const submissionCreateMatch = path.match(
    /^\/assignments\/([^/]+)\/submissions$/
  );
  if (submissionCreateMatch) {
    return getDemoSubmissionCreate(submissionCreateMatch[1]);
  }

  const submissionUpdateMatch = path.match(
    /^\/assignments\/([^/]+)\/submissions\/([^/]+)$/
  );
  if (submissionUpdateMatch) {
    return { success: true };
  }

  const exemptionCreateMatch = path.match(
    /^\/assignments\/([^/]+)\/exemption$/
  );
  if (exemptionCreateMatch) {
    return getDemoExemptionCreate();
  }

  const exemptionDeleteMatch = path.match(
    /^\/assignments\/exemptions\/([^/]+)\/delete$/
  );
  if (exemptionDeleteMatch) {
    return { success: true };
  }

  // Certificate request POST endpoint - triggers generation and returns status
  const certificateRequestMatch = path.match(
    /^\/user\/achievements\/(\d+)\/certificate$/
  );
  if (certificateRequestMatch) {
    return buildCertificateRequestResponse();
  }

  const assetUploadMatch = path.match(/^\/assignments\/submissions\/([^/]+)\/asset$/);
  if (assetUploadMatch) {
    return getDemoAssetUpload();
  }

  // del() routes through the POST handler in demo mode (demo router is GET/POST only).
  const assetDeleteMatch = path.match(/^\/assignments\/submissions\/([^/]+)\/asset\/\d+$/);
  if (assetDeleteMatch) {
    return {};
  }

  const finalizeMatch = path.match(/^\/assignments\/submissions\/([^/]+)\/finalize$/);
  if (finalizeMatch) {
    return { success: true };
  }

  return { success: true, message: "ok" };
};

/* ------------------------------------------------------------------ */
/*  URL parsing                                                       */
/* ------------------------------------------------------------------ */

const parsePath = (endpoint: string) => {
  const [rawPath, rawQuery = ""] = endpoint.split("?");
  return {
    path: rawPath.startsWith("/") ? rawPath : `/${rawPath}`,
    query: new URLSearchParams(rawQuery)
  };
};

/* ------------------------------------------------------------------ */
/*  Static GET responses                                              */
/* ------------------------------------------------------------------ */

const getStaticGetResponse = (path: string, query: URLSearchParams) => {
  if (path === "/impersonate/status") {
    return {
      is_impersonating: false,
      portal_admin_id: null,
      portal_user_id: null
    };
  }

  if (path === "/user/header-details") {
    // Deep-clone DEMO_GRADES and DEMO_USER_STATE on every request so
    // the source-of-truth objects remain mutable. POST handlers
    // (/user/state, /user/complete-orientation/:id) need to mutate
    // these between requests.
    return {
      student: { ...DEMO_STUDENT },
      grades: DEMO_GRADES.map(grade => ({ ...grade })),
      external_services: [],
      user_state: { ...DEMO_USER_STATE },
      practise_section_upload_enabled: true,
      activity_sheet_upload_enabled: true,
      chat_enabled: true,
      // Orientation is enabled so reviewers can walk through the
      // Grade 5 orientation flow. DEMO_GRADES controls which grades
      // still need orientation (Grade 5 starts incomplete).
      orientation_enabled: true
    };
  }

  if (path === "/user/family-selected-avatars") {
    return { selected_avatars: [5, 7] };
  }

  if (path === "/chat/history") {
    const lessonId = query.get("lesson_id") ?? "demo-lesson";
    return {
      session_id: "demo-session-1",
      lesson_id: lessonId,
      messages: [
        {
          id: "m-1",
          role: "assistant",
          content: "Hi! I am your demo study assistant. Ask me anything.",
          created_at: new Date().toISOString()
        }
      ]
    };
  }

  if (path === "/fun-fact/today") {
    // Demo fixture for the Fun Fact of the Day feature — mirrors the
    // production 204 contract exactly.
    //
    // In production, the backend returns 204 No Content when:
    //   1. The waffle flag `euka.enable_fun_fact` is off
    //   2. The grade code is NOT one of the Primary codes
    //      (P=901, F=1, G1=10, G2=20, G3=30, G4=40, G5=50, G6=60)
    //   3. The active content pool is empty
    //
    // In demo mode we enforce (2) only: the panel must NOT render on a
    // Secondary or Senior dashboard because it is a Primary-only feature
    // and the spec's Primary-only enforcement rule is the 204 contract.
    // Returning an empty object `{}` here is treated as null by
    // `getTodayFunFact` (see apps/learning/src/api/funFactApi.ts), which
    // flips the hook into status="hidden" and the panel renders nothing.
    //
    // For Primary grade codes we return a real payload so reviewers can
    // exercise the teaser -> reveal interaction on a Primary dashboard.
    // The content varies by group (early: P/F/G1-G3; upper: G4-G6) so
    // the rotation logic is visible at a glance.
    //
    // NOTE: A 2-second simulated network delay is applied in httpClient.ts
    // for this endpoint in demo mode (see demoPause). This makes the
    // loading state — dot loader inside the speech bubble (BLD2-376) —
    // visible during reviews without needing a slow real network.
    const grade = Number(query.get("grade") ?? "0");

    const PRIMARY_EARLY = [901, 1, 10, 20, 30]; // P, F, G1-G3
    const PRIMARY_UPPER = [40, 50, 60]; // G4-G6

    if (PRIMARY_EARLY.includes(grade)) {
      return {
        teaser: "Lemons float in water, but limes sink.",
        revealed_content:
          "Lemons float in water, but limes sink! Limes are a little bit " +
          "heavier on the inside than lemons, even though they look almost " +
          "the same size. That extra weight makes limes go straight to the " +
          "bottom."
      };
    }

    if (PRIMARY_UPPER.includes(grade)) {
      return {
        teaser: "A day on Venus is longer than a year on Venus.",
        revealed_content:
          "A day on Venus is longer than a year on Venus! Venus rotates so " +
          "slowly that it takes about 243 Earth days to spin once on its " +
          "axis, but it only takes 225 Earth days to orbit the Sun."
      };
    }

    // Non-Primary grade (70, 80, 90, 100, 110, 120) — hide the panel.
    // Empty object is normalised to null by getTodayFunFact.
    return {};
  }

  return undefined;
};

/* ------------------------------------------------------------------ */
/*  Pattern-matched GET responses                                     */
/* ------------------------------------------------------------------ */

const getPatternGetResponse = (path: string, query: URLSearchParams) => {
  const gradeStatsMatch = path.match(/^\/user\/dashboard-details\/(\d+)$/);
  if (gradeStatsMatch) {
    const gradeCode = Number(gradeStatsMatch[1]);
    const hasDemoAssignments = new Set([20, 50, 80, 100, 110]).has(gradeCode);

    return {
      success: true,
      message: "ok",
      data: {
        // Backend always populates assignments_url now; entry-point
        // visibility is gated by the per-grade-group UI flag, not the URL.
        assignments_url: hasDemoAssignments
          ? "https://portal.demo.euka.edu.au/assignments"
          : "",
        subjects_enabled: true,
        achievements_enabled: true,
        notifications: getDemoNotifications(gradeCode)
      }
    };
  }

  // /structure and /progress serve pre-baked fixtures so the prod merge runs
  // over the real BE contract shapes. most_recent_work is frozen in the fixture
  // — demo lesson completions no longer update the dashboard's recent-work panel.
  const structureMatch = path.match(/^\/course\/grade\/(\d+)\/structure$/);
  if (structureMatch) {
    const fixtures = structureFixtures as Record<string, unknown>;
    return fixtures[structureMatch[1]];
  }

  const progressMatch = path.match(/^\/user\/grade\/(\d+)\/progress$/);
  if (progressMatch) {
    const fixtures = progressFixtures as Record<string, unknown>;
    return fixtures[progressMatch[1]];
  }

  const materialsMatch = path.match(/^\/course\/grade\/(\d+)\/materials$/);
  if (materialsMatch) {
    return buildGradeMaterials();
  }

  const achievementsMatch = path.match(/^\/user\/achievements\/(\d+)$/);
  if (achievementsMatch) {
    return buildAchievements(Number(achievementsMatch[1]));
  }

  // Assignment GET endpoints
  const assignmentsListMatch = path.match(/^\/assignments\/grade\/(\d+)$/);
  if (assignmentsListMatch) {
    const gradeCode = Number(assignmentsListMatch[1]);
    const termParam = query.get("term");
    const term =
      termParam !== null && termParam !== "" ? Number(termParam) : undefined;
    const list = getDemoAssignmentsList(gradeCode, term);
    if (!list) return list;
    // term_summary + grade_summary are grade-wide; assembled here where the
    // grade's assessed_terms config is in scope.
    const assessedTerms =
      DEMO_GRADES.find(g => g.grade_code === gradeCode)?.assessed_terms ?? [];
    return { ...list, ...getDemoAssignmentSummaries(gradeCode, assessedTerms) };
  }

  const assignmentDetailMatch = path.match(/^\/assignments\/([^/]+)$/);
  if (assignmentDetailMatch) {
    const detail = getDemoAssignmentDetail(assignmentDetailMatch[1]);
    if (detail) return detail;
  }

  const workSamplesMatch = path.match(
    /^\/lesson\/grade\/(\d+)\/term\/(\d+)\/subject\/([^/]+)\/week\/(\d+)\/lesson\/(\d+)\/activity\/([^/]+)\/work-samples$/
  );
  if (workSamplesMatch) {
    return { work_samples: [] };
  }

  // BLD2-676: assignment-scoped work samples GET
  const assignmentWorkSamplesMatch = path.match(
    /^\/assignments\/([^/]+)\/work-samples$/
  );
  if (assignmentWorkSamplesMatch) {
    const stored = _demoAssignmentWorkSamples.get(assignmentWorkSamplesMatch[1]);
    return {
      work_samples: stored
        ? [
            {
              id: stored.uuid,
              comment: stored.comment,
              attachments: stored.attachments
            }
          ]
        : []
    };
  }

  const sectionContentMatch = path.match(
    /^\/lesson\/grade\/(\d+)\/term\/(\d+)\/subject\/([^/]+)\/week\/(\d+)\/lesson\/(\d+)\/activity\/([^/]+)$/
  );
  if (sectionContentMatch) {
    const [
      ,
      gradeCodeRaw,
      termIdRaw,
      subjectSlug,
      weekIdRaw,
      lessonIdRaw,
      section
    ] = sectionContentMatch;
    return buildSectionContent(
      Number(gradeCodeRaw),
      Number(termIdRaw),
      subjectSlug,
      Number(weekIdRaw),
      Number(lessonIdRaw),
      section
    );
  }

  return undefined;
};

/* ------------------------------------------------------------------ */
/*  Main entry point                                                  */
/* ------------------------------------------------------------------ */

export const getLearningDemoApiResponse = (
  method: HttpMethod,
  endpoint: string,
  payload?: unknown
): unknown | undefined => {
  const { path, query } = parsePath(endpoint);

  if (method === "POST") {
    return getDefaultPostResponse(path, payload);
  }

  const staticGetResponse = getStaticGetResponse(path, query);
  if (staticGetResponse !== undefined) {
    return staticGetResponse;
  }

  const patternGetResponse = getPatternGetResponse(path, query);
  if (patternGetResponse !== undefined) {
    return patternGetResponse;
  }

  if (path.startsWith("/core/cdn-signed-url/documents/activity_sheets")) {
    return buildDemoSignedUrl("activity-sheet");
  }

  if (path.startsWith("/core/cdn-signed-url/documents/answer_sheets")) {
    return buildDemoSignedUrl("answer-sheet");
  }

  return undefined;
};

/* ------------------------------------------------------------------ */
/*  Learnosity signing for demo section content                       */
/* ------------------------------------------------------------------ */

/**
 * If a demo section-content response contains an unsigned activity_request
 * (marked by `_unsigned: true`), call the Vite server's /learning/learnosity-sign
 * endpoint to get a properly signed Learnosity Items API init object.
 *
 * When the activity_request has already been pre-signed at build time (no
 * `_unsigned` flag), this function is a no-op pass-through — no server call
 * is needed and the static S3 demo works without a signing endpoint.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const signDemoActivityRequest = async (response: any): Promise<any> => {
  const activityRequest = response?.activity_request;

  // Already signed (build-time pre-signing) or no activity_request at all
  if (!activityRequest?._unsigned) return response;

  // Local dev path: call the Vite dev server's signing endpoint on-demand
  try {
    const signResponse = await fetch(
      `${window.location.origin}/learning/learnosity-sign`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          activity_uuid: activityRequest._activity_uuid,
          item_uuids: activityRequest._item_uuids
        })
      }
    );

    if (!signResponse.ok) {
      console.warn(
        "[learning-demo] Learnosity signing failed, using unsigned fallback"
      );
      return response;
    }

    const signed = await signResponse.json();
    return {
      ...response,
      activity_request: signed
    };
  } catch (err) {
    console.warn("[learning-demo] Learnosity signing error:", err);
    return response;
  }
};
